import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useAuth } from "@/app/providers/useAuth";
import { routes } from "@/app/navigation/routes";
import {
  couponService,
  paymentSubscriptionService,
  type CheckoutStatusData,
} from "@/features/protest/services";
import { handleProceedToMultiPayment } from "@/features/protest/viewModal/stripe";
import { useReferralSettings } from "@/features/referrals/viewmodel";
import { toastService } from "@/shared/toast";
import { tokenService } from "@/services/auth/token.service";
import { propertyService } from "@/features/properties/model/propertyServices";
import { isValidUUID } from "@/features/properties/model/guestSession";

type PaymentIntentData = {
  client_secret: string;
  payment_intent_id: string;
};

/** Fallback while price-descriptions loads or if the request fails. */
const DEFAULT_BASE_PROTEST_AMOUNT = 49;
const ANNUAL_PROTEST_PRICE_NAME = "annual_protest";
const MIN_PAYABLE_AMOUNT = 1;
/** Sent to coupon apply API; set to null if your backend expects no category. */
const PROTEST_COUPON_CATEGORY: string | null = "protest";

export const useProtestPaymentHero = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const stripe = useStripe();
  const elements = useElements();
  const { statistics } = useReferralSettings();

  const [isProcessing, setIsProcessing] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [useReferralPoints, setUseReferralPoints] = useState(false);
  const [paymentIntentData, setPaymentIntentData] = useState<PaymentIntentData | null>(null);
  const [couponCodeInput, setCouponCodeInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<{
    code: string;
    discountAmount: number;
    finalAmount?: number;
  } | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [baseProtestAmount, setBaseProtestAmount] = useState(DEFAULT_BASE_PROTEST_AMOUNT);
  const [activePriceId, setActivePriceId] = useState<string>("price_1TshefDJVPEOLc888LHlSDrJ");
  const [checkoutStatusData, setCheckoutStatusData] = useState<CheckoutStatusData | null>(null);
  const [isVerifyingCheckout, setIsVerifyingCheckout] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const searchParams = new URLSearchParams(location.search);
    const sessionId = searchParams.get("session_id");

    if (sessionId) {
      setIsVerifyingCheckout(true);
      paymentSubscriptionService
        .getCheckoutStatus(sessionId)
        .then((res) => {
          if (cancelled) return;
          const statusPayload =
            (res as unknown as { data?: { data?: CheckoutStatusData } })?.data?.data ||
            (res as unknown as { data?: CheckoutStatusData })?.data ||
            (res as unknown as CheckoutStatusData);

          if (statusPayload) {
            setCheckoutStatusData(statusPayload);
            if (statusPayload.checkout_status === "paid" || statusPayload.is_complete) {
              toastService.show("Payment completed successfully!", "success");
            }
          }
        })
        .catch((err) => {
          console.error("Failed to fetch checkout status:", err);
        })
        .finally(() => {
          if (!cancelled) setIsVerifyingCheckout(false);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [location.search]);

  const getSelectedProperties = useCallback((): { property_id?: string; selected?: boolean }[] => {
    const stateProps = (
      location.state as { selectedProperties?: { property_id?: string; selected?: boolean }[] }
    )?.selectedProperties;
    if (Array.isArray(stateProps) && stateProps.length > 0) {
      return stateProps.filter((p) => p.selected !== false);
    }
    try {
      const storedPropsRaw = localStorage.getItem("protest_selected_properties");
      if (storedPropsRaw) {
        const parsed = JSON.parse(storedPropsRaw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.filter((p: { selected?: boolean }) => p.selected !== false);
        }
      }
    } catch (e) {
      console.error("Error reading stored protest properties:", e);
    }
    return [];
  }, [location.state]);

  const selectedPropertiesCount = useMemo(() => {
    const selectedList = getSelectedProperties();
    return selectedList.length > 0 ? selectedList.length : 1;
  }, [getSelectedProperties]);

  const totalBaseAmount = useMemo(() => {
    return selectedPropertiesCount * baseProtestAmount;
  }, [selectedPropertiesCount, baseProtestAmount]);

  const availableReferralPoints = Number(statistics?.totalPointsUsed ?? 0);
  const subtotalAfterReferral = useReferralPoints
    ? Math.max(MIN_PAYABLE_AMOUNT, totalBaseAmount - availableReferralPoints)
    : totalBaseAmount;

  const payableAmount = (() => {
    if (!appliedCoupon) return subtotalAfterReferral;
    if (appliedCoupon.finalAmount != null) {
      return Math.max(MIN_PAYABLE_AMOUNT, appliedCoupon.finalAmount);
    }
    return Math.max(MIN_PAYABLE_AMOUNT, subtotalAfterReferral - appliedCoupon.discountAmount);
  })();
  console.log(payableAmount, totalBaseAmount, "payableAmount");

  const persistedUser = tokenService.getUser();
  const effectiveUserId = user?.id ?? persistedUser?.id;

  const canStartProtest = useMemo(() => true, []);

  useEffect(() => {
    if (useReferralPoints) {
      setAppliedCoupon(null);
      setCouponError(null);
      setCouponCodeInput("");
    }
  }, [useReferralPoints]);

  useEffect(() => {
    let cancelled = false;

    const loadBaseProtestAmount = async () => {
      try {
        const descriptions = await propertyService.getPriceDescriptions();
        const annualProtest = descriptions.find(
          (item) => item.name === ANNUAL_PROTEST_PRICE_NAME && item.status === "active"
        );
        if (annualProtest?.price_id) {
          setActivePriceId(annualProtest.price_id);
        }
        const amount = Number(annualProtest?.amount);
        if (!cancelled && Number.isFinite(amount) && amount > 0) {
          setBaseProtestAmount(amount);
        }
      } catch (error) {
        console.error("Failed to load protest price description:", error);
      }
    };

    void loadBaseProtestAmount();

    return () => {
      cancelled = true;
    };
  }, []);

  const onStartProtest = async () => {
    const selectedList = getSelectedProperties();

    const propertyIds: string[] = Array.from(
      new Set(
        selectedList
          .map((p: Record<string, unknown>) => {
            // Priority 1: Direct property_id property
            const pid = String(p.property_id || p.id || "");
            if (isValidUUID(pid)) {
              return pid;
            }

            // Priority 2: Check rawDetails for property_id
            const raw = (p.rawDetails || {}) as Record<string, unknown>;
            const rawPid = String(raw.property_id || raw.id || "");
            if (isValidUUID(rawPid)) {
              return rawPid;
            }

            // Priority 3: Return pid string if present (never fallback to api_property_id or random UUID if property_id exists)
            if (pid.trim()) {
              return pid.trim();
            }

            return undefined;
          })
          .filter((id: string | undefined): id is string => Boolean(id))
      )
    );

    if (propertyIds.length === 0) {
      toastService.show("No selected properties found. Please select a property.", "error");
      return;
    }

    try {
      setIsProcessing(true);
      const intent = await handleProceedToMultiPayment({
        amount: totalBaseAmount,
        priceId: activePriceId,
        propertyIds,
        userId: effectiveUserId ? effectiveUserId : null,
        year: 2026,
        useWallet: useReferralPoints,
        couponCode: appliedCoupon?.code ?? "",
      });

      const checkoutUrl = (intent?.checkout_url as string | undefined) || undefined;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }

      const clientSecret = intent?.client_secret as string | undefined;
      if (clientSecret) {
        setPaymentIntentData({
          client_secret: clientSecret,
          payment_intent_id: (intent?.payment_intent_id as string) || "",
        });
        setIsPaymentModalOpen(true);
      }
    } catch (error) {
      console.error("Payment intent error:", error);
      toastService.show("Unable to start payment. Please try again.", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const onConfirmCardPayment = async () => {
    try {
      setIsProcessing(true);

      if (paymentIntentData?.client_secret?.startsWith("mock_")) {
        // Simulating guest payment processing
        await new Promise((resolve) => setTimeout(resolve, 800));
      } else if (stripe && elements && paymentIntentData) {
        const card = elements.getElement(CardElement);
        if (!card) {
          toastService.show("Please enter your card details.", "error");
          setIsProcessing(false);
          return;
        }

        const result = await stripe.confirmCardPayment(paymentIntentData.client_secret, {
          payment_method: { card },
        });

        if (result.error) {
          throw new Error(result.error.message || "Payment failed");
        }

        if (result.paymentIntent?.status === "succeeded") {
          try {
            await paymentSubscriptionService.updatePaymentStatus({
              payment_intent_id: paymentIntentData.payment_intent_id,
            });
          } catch (e) {
            console.warn("Payment status API update handled locally:", e);
          }
        }
      }

      setPaymentIntentData(null);
      setIsPaymentModalOpen(false);
      toastService.show("Payment successful! Please complete owner details.", "success");
      navigate(routes.selectPropertyType, {
        state: {
          fromPayment: true,
        },
      });
    } catch (error) {
      console.error("Payment confirm error:", error);
      toastService.show("Unable to complete payment. Please try again.", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const onApplyCoupon = async () => {
    if (useReferralPoints) {
      setCouponError("Turn off referral rewards to use a promo code.");
      return;
    }
    const code = couponCodeInput.trim();
    if (!code) {
      setCouponError("Enter a promo code.");
      return;
    }
    const userId = user?.id != null ? String(user.id) : "null";
    // if (!userId) {
    //   setCouponError("You must be signed in to apply a promo code.");
    //   return;
    // }

    setCouponError(null);
    setIsApplyingCoupon(true);
    try {
      const breakdown = await couponService.applyCouponCode({
        code,
        user_id: userId,
        order_amount: subtotalAfterReferral,
        category: PROTEST_COUPON_CATEGORY,
      });
      if (breakdown.discountAmount <= 0 && breakdown.finalAmount == null) {
        throw new Error("This promo code did not change your order total.");
      }
      setUseReferralPoints(false);
      const finalFromApi = breakdown.finalAmount;
      setAppliedCoupon({
        code,
        discountAmount: breakdown.discountAmount,
        finalAmount: finalFromApi != null ? Math.max(MIN_PAYABLE_AMOUNT, finalFromApi) : undefined,
      });
    } catch (e) {
      setAppliedCoupon(null);
      setCouponError(e instanceof Error ? e.message : "Unable to apply promo code.");
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const onRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponError(null);
  };

  return {
    isProcessing,
    isPaymentModalOpen,
    setIsPaymentModalOpen,
    useReferralPoints,
    setUseReferralPoints,
    availableReferralPoints,
    payableAmount,
    canStartProtest,
    onStartProtest,
    onConfirmCardPayment,
    baseProtestAmount,
    unitProtestAmount: baseProtestAmount,
    selectedPropertiesCount,
    totalBaseAmount,
    minPayableAmount: MIN_PAYABLE_AMOUNT,
    couponCodeInput,
    setCouponCodeInput,
    appliedCoupon,
    couponError,
    isApplyingCoupon,
    onApplyCoupon,
    onRemoveCoupon,
    subtotalAfterReferral,
    checkoutStatusData,
    isVerifyingCheckout,
  };
};
